/**
 * Demo component showcasing the generic table with styling matching the design
 */

import { Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { finalize, switchMap, tap } from 'rxjs';

import { EmployeeRow, TableExampleService } from './table-example.service';
import { MatrixGenericTableComponent } from '../../../../shared/components/generic-table/generic-table.component';
import { GenericTableColumn } from '../../../../shared/components/generic-table/models/table-column.model';
import { TableQuery } from '../../../../shared/components/generic-table/models/table-query.model';

/**
 * @iconByValue: The icon to display based on the value
 * @iconClassByValue: The class to apply to the icon
 * @labelByValue: The label to display based on the value
 * @textClassByValue: The class to apply to the text
 */
const ACTION_ALERT_CONFIG = {
  iconByValue: {
    attention: 'pi pi-exclamation-circle',
    none: '',
  } as const,
  iconClassByValue: {
    attention: 'text-red-500',
    none: '',
  } as const,
  labelByValue: {
    attention: 'Attention Req!',
    none: '',
  } as const,
  textClassByValue: {
    // attention: 'text-red-400 font-medium', // This sets the color of the text
    none: 'text-white',
  } as const,
};

/**
 * @iconByValue: The icon to display based on the value
 * @iconClassByValue: The class to apply to the icon
 * @labelByValue: The label to display based on the value
 * @textClassByValue: The class to apply to the text
 */
const EMPLOYEE_STATUS_CONFIG = {
  iconByValue: {
    Unassigned: 'pi pi-question-circle',
    Referral: 'pi pi-wifi',
    'Under Invest': 'pi pi-search',
    'Rev In Progress': 'pi pi-sync',
    Monitor: 'pi pi-eye',
    Terminated: 'pi pi-ban',
    Suspended: 'pi pi-exclamation-triangle',
    'Requires Review': 'pi pi-bell',
  } as const,
  iconClassByValue: {
    Unassigned: 'text-slate-400',
    Referral: 'text-cyan-400',
    'Under Invest': 'text-amber-400',
    'Rev In Progress': 'text-cyan-400',
    Monitor: 'text-slate-300',
    Terminated: 'text-red-400',
    Suspended: 'text-amber-400',
    'Requires Review': 'text-orange-400',
  } as const,
  textClassByValue: {
    // You can use custom colors for the text, or leave the default color (white)
    Unassigned: 'text-red-400',
    'Requires Review': 'text-blue-400',
  } as const,
};

@Component({
  selector: 'app-table-example',
  templateUrl: './table-example.component.html',
  standalone: true,
  imports: [MatrixGenericTableComponent],
})
export class TableExampleComponent {
  private readonly tableExampleService = inject(TableExampleService);
  private readonly destroyRef = inject(DestroyRef);

  columns = signal<GenericTableColumn<EmployeeRow>[]>([
    {
      field: 'actionAlert',
      header: 'Action Alert',
      sortable: true,
      filterable: true,
      type: 'status',
      status: ACTION_ALERT_CONFIG, // This is the custom configuration we want to setup
    },
    {
      field: 'employeeStatus',
      header: 'Employee Status',
      sortable: true,
      filterable: true,
      type: 'status',
      status: EMPLOYEE_STATUS_CONFIG, // This is the custom configuration we want to setup
    },
    {
      field: 'employeeName',
      header: 'Employee',
      sortable: true,
      filterable: true,
      type: 'link',
      link: { // When a column is a link, we can configure the link to be a router link or a href link
        routerLink: (row) => ['/employee', row.id],
        label: (row) => row.employeeName,
      },
    },
    {
      field: 'employeeId',
      header: 'Employee ID',
      sortable: true,
      filterable: true,
      align: 'right',
    },
    {
      field: 'role',
      header: 'Role',
      sortable: true,
      filterable: true,
      align: 'center',
    },
    {
      field: 'newVar',
      header: 'New Var',
      sortable: true,
      filterable: true,
      type: 'link',
      link: {
        routerLink: (row) => ['/variances', row.id, 'new'],
        label: (_, value) => String(value),
      },
    },
    {
      field: 'openVar',
      header: 'Open Var',
      sortable: true,
      filterable: true,
      type: 'link',
      link: {
        routerLink: (row) => ['/variances', row.id, 'open'],
        label: (_, value) => String(value),
      },
    },
    {
      field: 'reviewed',
      header: 'Reviewed',
      sortable: true,
      filterable: true,
      type: 'link',
      link: {
        routerLink: (row) => ['/variances', row.id, 'reviewed'],
        label: (_, value) => String(value),
      },
    },
    {
      field: 'highRiskMedVar',
      header: 'High Risk Med Var',
      sortable: true,
      filterable: true,
      type: 'link',
      link: {
        routerLink: (row) => ['/variances', row.id, 'high-risk'],
        label: (_, value) => String(value),
      },
    },
  ]);

  // Table state
  data = signal<EmployeeRow[]>([]);
  totalRecords = signal(0);
  loading = signal(false);
  rows = signal(10);

  private readonly querySig = signal<TableQuery>({ page: 0, rows: this.rows() });

  constructor() {
    toObservable(this.querySig)
      .pipe(
        tap((q) => {
          this.rows.set(q.rows);
          this.loading.set(true);
        }),
        switchMap((q) =>
          this.tableExampleService.getData(q).pipe(finalize(() => this.loading.set(false)))
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((res) => {
        this.data.set(res.data);
        this.totalRecords.set(res.totalRecords);
      });
  }

  onQueryChange(query: TableQuery): void {
    this.querySig.set(query);
  }
}
